import React, { useEffect, useState } from 'react';
import { Phone, PhoneOff, Mic, MoreVertical } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
interface ActiveCallScreenProps {
  onEndCall: () => void;
}
export function ActiveCallScreen({
  onEndCall
}: ActiveCallScreenProps) {
  const {
    isLight
  } = useTheme();
  const [aiActive, setAiActive] = useState(false);
  const [transcript, setTranscript] = useState<string[]>([]);
  useEffect(() => {
    if (aiActive) {
      // Simulate live transcript
      const timeouts = [setTimeout(() => setTranscript(prev => [...prev, "AI: Hello, I'm answering for Alex..."]), 1000), setTimeout(() => setTranscript(prev => [...prev, "Caller: Hi, I'm calling about the delivery."]), 3000), setTimeout(() => setTranscript(prev => [...prev, "AI: I can help with that. What's the tracking number?"]), 5000)];
      return () => timeouts.forEach(clearTimeout);
    }
  }, [aiActive]);
  return <div className="h-full flex flex-col relative overflow-hidden" data-id="element-403">
      {/* Background with blur */}
      <div className={`absolute inset-0 z-0 transition-colors duration-500 ${isLight ? 'bg-slate-100' : 'bg-dark'}`} data-id="element-404">
        <div className={`absolute inset-0 bg-[url('https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&w=800&q=80')] bg-cover bg-center blur-2xl scale-110 transition-opacity duration-500 ${isLight ? 'opacity-5' : 'opacity-10 grayscale'}`} data-id="element-405" />
        <div className={`absolute inset-0 bg-gradient-to-b transition-colors duration-500 ${isLight ? 'from-slate-100/80 via-slate-100/90 to-slate-100' : 'from-dark/80 via-dark/90 to-dark'}`} data-id="element-406" />
        <div className={`absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl pointer-events-none transition-colors duration-500 ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-407" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col p-6 pt-12" data-id="element-408">
        <div className="flex-1 flex flex-col items-center text-center" data-id="element-409">
          <div className={`w-28 h-28 rounded-full border flex items-center justify-center mb-6 shadow-2xl transition-colors duration-500 ${isLight ? 'bg-white border-slate-200 shadow-slate-300/50' : 'bg-dark-card border-white/10 shadow-black/50'}`} data-id="element-410">
            <span className={`text-4xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-411">
              JD
            </span>
          </div>
          <h2 className={`text-3xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-412">
            John Doe
          </h2>
          <p className={`font-medium animate-pulse transition-colors duration-500 ${isLight ? 'text-vexu-600 drop-shadow-[0_0_4px_rgba(0,200,83,0.3)]' : 'text-vexu drop-shadow-[0_0_8px_rgba(0,200,83,0.5)]'}`} data-id="element-413">
            {aiActive ? 'AI is handling this call...' : 'Incoming Call...'}
          </p>

          {aiActive && <div className={`mt-8 w-full max-w-xs backdrop-blur-xl rounded-2xl p-5 border h-48 overflow-y-auto shadow-2xl transition-colors duration-500 ${isLight ? 'bg-white/80 border-slate-200/60 shadow-slate-300/50' : 'bg-dark-card/60 border-white/10 shadow-black/50'}`} data-id="element-414">
              <div className="space-y-3 text-left" data-id="element-415">
                {transcript.map((line, i) => <p key={i} className={`text-sm ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-416">
                    {line}
                  </p>)}
                {transcript.length === 0 && <div className={`flex items-center gap-1 text-sm ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-417">
                    <span className={`w-1.5 h-1.5 rounded-full animate-bounce ${isLight ? 'bg-slate-400' : 'bg-zinc-500'}`} style={{
                animationDelay: '0ms'
              }} data-id="element-418" />
                    <span className={`w-1.5 h-1.5 rounded-full animate-bounce ${isLight ? 'bg-slate-400' : 'bg-zinc-500'}`} style={{
                animationDelay: '150ms'
              }} data-id="element-419" />
                    <span className={`w-1.5 h-1.5 rounded-full animate-bounce ${isLight ? 'bg-slate-400' : 'bg-zinc-500'}`} style={{
                animationDelay: '300ms'
              }} data-id="element-420" />
                  </div>}
              </div>
            </div>}
        </div>

        <div className="mt-auto space-y-4" data-id="element-421">
          {!aiActive ? <>
              <Button onClick={() => setAiActive(true)} className={`w-full h-14 rounded-full shadow-lg border-0 transition-colors duration-500 ${isLight ? 'bg-vexu hover:bg-vexu-600 text-white shadow-vexu/20' : 'bg-vexu hover:bg-vexu-400 text-black shadow-vexu/20'}`} data-id="element-422">
                <div className="flex items-center justify-center gap-2" data-id="element-423">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isLight ? 'bg-white/20' : 'bg-black/10'}`} data-id="element-424">
                    <Mic className="w-4 h-4" data-id="element-425" />
                  </div>
                  <span className="font-semibold text-lg" data-id="element-426">
                    Answer with AI Message
                  </span>
                </div>
              </Button>

              <div className="grid grid-cols-2 gap-4" data-id="element-427">
                <button className={`h-14 rounded-full backdrop-blur-md border font-medium transition-colors ${isLight ? 'bg-white/80 border-slate-200 text-slate-700 hover:bg-white shadow-sm' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-428">
                  Message
                </button>
                <button onClick={onEndCall} className={`h-14 rounded-full backdrop-blur-md border font-medium flex items-center justify-center gap-2 transition-colors ${isLight ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100 shadow-sm' : 'bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20'}`} data-id="element-429">
                  <PhoneOff className="w-5 h-5" data-id="element-430" />
                  Decline
                </button>
              </div>
            </> : <div className="grid grid-cols-2 gap-4" data-id="element-431">
              <button className={`h-14 rounded-full backdrop-blur-md border font-medium flex items-center justify-center gap-2 transition-colors ${isLight ? 'bg-white/80 border-slate-200 text-slate-700 hover:bg-white shadow-sm' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}`} data-id="element-432">
                <Phone className="w-5 h-5" data-id="element-433" />
                Take Over
              </button>
              <button onClick={onEndCall} className={`h-14 rounded-full font-medium flex items-center justify-center gap-2 shadow-lg transition-colors ${isLight ? 'bg-red-500 text-white hover:bg-red-600 shadow-red-500/20' : 'bg-red-500/90 text-white hover:bg-red-500 shadow-red-500/20'}`} data-id="element-434">
                <PhoneOff className="w-5 h-5" data-id="element-435" />
                End Call
              </button>
            </div>}
        </div>
      </div>
    </div>;
}